<script setup lang="ts">

import {computed, defineProps, onMounted, ref, watch} from "vue";
import {usePlanetStore} from "@/stores/planetStore";
import Backend from "@/models/backend";
import PlanetTaxSetting from "@/components/Sections/Planets/PlanetTaxSetting.vue";
import { useGameStore } from '@/stores/gameStore'
import { useI18n } from 'vue-i18n'
import RangeSlider from '@/components/Elements/Forms/RangeSlider.vue'

const { t } = useI18n()

/**
 * Props
 */
const props = defineProps({
  planetId: {
    type: Number,
    required: true
  }
});

/**
 * Stores / models
 */
const planetStore = usePlanetStore()
const gameStore = useGameStore()
const backend = new Backend()

/**
 * Variables
 */
const foodSlider = ref(0);
const scienceSlider = ref(0);
const productionSlider = ref(0);

/**
 * Computed
 */
const planetData = computed(() => planetStore.findPlanetById(props.planetId)!)

/**
 * Functions
 */
const updateSlidersForPlanet = () => {
  if (planetData.value) {
    foodSlider.value = planetData.value.populationFoodPercentage || 0;
    scienceSlider.value = planetData.value.populationSciencePercentage || 0;
    productionSlider.value = planetData.value.populationProductionPercentage || 0;
  } else {
    foodSlider.value = 0;
    scienceSlider.value = 0;
    productionSlider.value = 0;
  }
};

const adjustSliders = () => {
  backend.updatePlanetPopulation(
    [props.planetId],
    Math.floor(foodSlider.value),
    Math.floor(scienceSlider.value),
    Math.floor(productionSlider.value)
  );
};

/**
 * Watchers
 */
watch(() => props.planetId, (newVal, oldVal) => {
  if (newVal !== oldVal) {
    updateSlidersForPlanet();
  }
}, { immediate: true });

// Watch for external updates to planetData
watch(planetData, (newPlanetData) => {
  if (newPlanetData) {
    foodSlider.value = newPlanetData.populationFoodPercentage || 0;
    scienceSlider.value = newPlanetData.populationSciencePercentage || 0;
    productionSlider.value = newPlanetData.populationProductionPercentage || 0;
  }
}, { immediate: true, deep: true });

/**
 * Lifecycle
 */
onMounted(() => {
  updateSlidersForPlanet();
});

</script>

<template>
  <div class="grid grid-cols-2 2xl:grid-cols-4">
    <div class="flex-1 p-2 flex">
      <img class="hidden md:block w-10 h-10 mr-3 rounded-3xl" :src="`${gameStore.cdnUrl}images/population/64/food.webp`" :alt="t('planets.population.settings.farmers')" :title="t('planets.population.settings.farmers')">
      <div class="relative mb-6 flex-grow">
        <RangeSlider v-model="foodSlider" :min="0" :max="100" :step="1" @update:modelValue="adjustSliders" />
        <span class="text-subtext absolute start-0 -bottom-6">{{ t('planets.population.settings.farmers') }}: <span class="text-metal-300">{{ planetData.populationWorkingOnFood }}</span></span>
      </div>
    </div>

    <div class="flex-1 p-2 flex">
      <img class="hidden md:block w-10 h-10 mr-3 rounded-3xl" :src="`${gameStore.cdnUrl}images/population/64/production.webp`" :alt="t('planets.population.settings.workers')" :title="t('planets.population.settings.workers')">
      <div class="relative mb-6 flex-grow">
        <RangeSlider v-model="productionSlider" :min="0" :max="100" :step="1" @update:modelValue="adjustSliders" />
        <span class="text-subtext absolute start-0 -bottom-6">{{ t('planets.population.settings.workers') }}: <span class="text-metal-300">{{ planetData.populationWorkingOnProduction }}</span></span>
      </div>
    </div>

    <div class="flex-1 p-2 flex">
      <img class="hidden md:block w-10 h-10 mr-3 rounded-3xl" :src="`${gameStore.cdnUrl}images/population/64/research.webp`" :alt="t('planets.population.settings.scientists')" :title="t('planets.population.settings.scientists')">
      <div class="relative mb-6 flex-grow">
        <RangeSlider v-model="scienceSlider" :min="0" :max="100" :step="1" @update:modelValue="adjustSliders" />
        <span class="text-subtext absolute start-0 -bottom-6 whitespace-nowrap">{{ t('planets.population.settings.scientists') }}: <span class="text-metal-300">{{ planetData.populationWorkingOnScience }}</span></span>
      </div>
    </div>

    <div class="flex-1 p-2 flex">
      <PlanetTaxSetting :planetId="planetData.id"></PlanetTaxSetting>
    </div>
  </div>
</template>

<style scoped lang="scss">
.planetHeader {
  height: 200px;
  background-size: cover;
  background-position: top;
}
.table-td {
  height: 50px !important;
  width: 50px;
}
</style>